/**
 * Icon set.
 *
 * Drawn as SVG rather than emoji. Emoji render differently on every platform,
 * can't be tinted, don't align to a text baseline and don't scale with a token
 * — the earlier build used them for itinerary types and it showed.
 *
 * One visual language throughout: 1.6 stroke, round caps and joins, 24-unit
 * grid, currentColor.
 */

export type IconName =
  // Navigation
  | 'dashboard'
  | 'tours'
  | 'tickets'
  | 'database'
  | 'export'
  | 'account'
  // Appearance
  | 'sun'
  | 'moon'
  | 'auto'
  // Itinerary types
  | 'key'
  | 'truck'
  | 'mic'
  | 'door'
  | 'star'
  | 'box'
  | 'plane'
  | 'planeArrive'
  | 'bed'
  | 'people'
  | 'clock'
  | 'food'
  | 'van'
  | 'camera'
  | 'shirt'
  | 'sofa'
  | 'calendar'
  // Actions & chrome
  | 'plus'
  | 'share'
  | 'edit'
  | 'trash'
  | 'chevronLeft'
  | 'chevronRight'
  | 'chevronDown'
  | 'list'
  | 'grid'
  | 'bell'
  | 'close'
  | 'mapPin'
  | 'search'

const PATHS: Record<IconName, string> = {
  // -- Navigation ----------------------------------------------------------
  dashboard: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',
  tours: 'M8 3v3M16 3v3M4 8h16M5 6h14a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z',
  tickets: 'M3 9V7a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v2a2 2 0 0 0 0 4v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2a2 2 0 0 0 0-4zM12 6v2M12 11v2M12 16v2',
  database: 'M4 5.5A2.5 2.5 0 0 1 6.5 3H19v15H6.5A2.5 2.5 0 0 0 4 20.5zM4 5.5v15',
  export: 'M12 15V3M8 7l4-4 4 4M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3',
  account: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4.5 20a7.5 7.5 0 0 1 15 0',

  // -- Appearance ----------------------------------------------------------
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10zM12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4',
  moon: 'M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z',
  auto: 'M12 3a9 9 0 0 0 0 18zM12 3a9 9 0 0 1 0 18',

  // -- Itinerary types -----------------------------------------------------
  key: 'M14.5 9.5a3.5 3.5 0 1 1 3.5-3.5M14.5 9.5L4 20v1h3v-2h2v-2h2l3.5-3.5',
  truck: 'M3 7h11v9H3zM14 10h4l3 3v3h-7zM7.5 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM17.5 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
  mic: 'M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zM5 11a7 7 0 0 0 14 0M12 18v3',
  door: 'M4 20h16M15 20V4H6v16M12 12v1',
  star: 'M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z',
  box: 'M3.5 7.5L12 3l8.5 4.5v9L12 21l-8.5-4.5zM3.5 7.5L12 12M20.5 7.5L12 12M12 12v9',
  plane: 'M21 15v-2l-8-5V4.5a1.5 1.5 0 0 0-3 0V8l-8 5v2l8-2.5V17l-2 1.5V20l3.5-1 3.5 1v-1.5L13 17v-4.5z',
  planeArrive: 'M3 20h18M4 13l2.5.7 3-3L5 7.4l1.6-.5 5 2.7 3.6-3.6a2 2 0 0 1 2.8 2.8L14.4 12l2.7 5-.5 1.6-3.3-4.5-3 3L14 17',
  bed: 'M3 18v-5h18v5M3 13V7M3 13h18M7.5 10.5h4M14 10.5h3.5a3.5 3.5 0 0 1 3.5 3.5',
  people: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2.5 20a6.5 6.5 0 0 1 13 0M16 4.3a3.5 3.5 0 0 1 0 6.4M18 13.5a6.5 6.5 0 0 1 3.5 5.8',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3.5 2',
  food: 'M6 3v8a2 2 0 0 0 4 0V3M8 11v10M17 3c-1.5 1.5-2 3.5-2 6s.5 3 2 3v9M17 3v9',
  van: 'M3 16V8h13l4 4v4M3 16h18M7.5 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM17.5 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
  camera: 'M12 17.5a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM3 19V8h4l2-3h6l2 3h4v11z',
  shirt: 'M8.5 3L4 5.5 5.5 10 8 9v11h8V9l2.5 1L20 5.5 15.5 3a3.5 3.5 0 0 1-7 0z',
  sofa: 'M4 12V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4M3 12a2 2 0 0 1 4 0v4h10v-4a2 2 0 0 1 4 0v6H3zM7 12h10',
  calendar: 'M8 3v3M16 3v3M4 9h16M5 6h14a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z',

  // -- Actions & chrome ----------------------------------------------------
  plus: 'M12 5v14M5 12h14',
  share: 'M12 15V3M8.5 6.5L12 3l3.5 3.5M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6',
  edit: 'M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17zM14.5 6.5l3 3',
  trash: 'M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13h10l1-13M10 11v6M14 11v6',
  chevronLeft: 'M15 5l-7 7 7 7',
  chevronRight: 'M9 5l7 7-7 7',
  chevronDown: 'M5 9l7 7 7-7',
  list: 'M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01',
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  bell: 'M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6M13.7 20a2 2 0 0 1-3.4 0',
  close: 'M6 6l12 12M18 6L6 18',
  mapPin: 'M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11zM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM16 16l5 5',
}

export default function Icon({
  name,
  size = 18,
  strokeWidth = 1.6,
  className,
  style,
}: {
  name: IconName
  size?: number
  strokeWidth?: number
  className?: string
  style?: React.CSSProperties
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
      style={{ flex: 'none', ...style }}
    >
      <path d={PATHS[name]} />
    </svg>
  )
}

/** The glyph for an `ItineraryItemType.rawValue`. */
export function itineraryIcon(type: string): IconName {
  switch (type) {
    case 'venueAccess':
      return 'key'
    case 'loadIn':
      return 'truck'
    case 'soundcheck':
      return 'mic'
    case 'doors':
      return 'door'
    case 'headline':
      return 'star'
    case 'packOut':
      return 'box'
    case 'flight':
      return 'plane'
    case 'arrival':
      return 'planeArrive'
    case 'hotel':
      return 'bed'
    case 'meeting':
      return 'people'
    case 'freeTime':
      return 'clock'
    case 'catering':
      return 'food'
    case 'travel':
      return 'van'
    case 'content':
      return 'camera'
    case 'merch':
      return 'shirt'
    case 'lounge':
      return 'sofa'
    default:
      return 'calendar'
  }
}
